import { useEffect, useState } from "react";
import CustomButton from "../../../../components/custom/CustomButton";
import {
  Input,
  Row,
  Typography,
  Col,
  Card,
  Select,
  Form,
  DatePicker,
  Divider,
  List,
  Button,
} from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { AddressModal } from "./BranchForm";

const { Text } = Typography;

const EditBranchForm = ({
  parentBranch = [],
  officeTypeOptions = [],
  nearestBranchOptions = [],
  countryIdData = [],
  stateProvinceIdData = [],
  addressTypeIdData = [],
  addressList = [],
  setAddressList,
  isModalOpen,
  setIsModalOpen,
  session,
  setIsLoading,
  cnt,
  apiConfig,
  BOM,
  selectedBranchKey,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [editingIndex, setEditingIndex] = useState(null);
  const currentYear = dayjs().year();

  const disabledDate = (current) =>
    current.year() !== currentYear || current > dayjs().endOf("day");

  useEffect(() => {
    getBranchById(selectedBranchKey.id);
    getAddressById(selectedBranchKey.id);
  }, []);

  const getBranchById = (id) => {
    const url = `${apiConfig.OfficeOutlet}/${id}?isOutlet=false`;

    setIsLoading(true);
    BOM.FetchReqAction({ session }, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        const {
          name,
          externalId,
          openingDate,
          parentId,
          officeTypeId,
          nearestBranchId,
        } = res;

        const date = Array.isArray(openingDate)
          ? dayjs(new Date(openingDate[0], openingDate[1] - 1, openingDate[2]))
          : dayjs();

        form.setFieldsValue({
          name,
          externalId,
          openedOn: date,
          parentBranch: parentId || parentBranch[0]?.value,
          officeTypeId: officeTypeId || undefined,
          nearestBranchId: nearestBranchId || undefined,
        });

        setIsLoading(false);
      }
    });
  };

  const getAddressById = (id) => {
    const url = `${apiConfig.AddressOffice}/${id}/addresses`;

    setIsLoading(true);
    BOM.FetchReqAction({ session }, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        if (Array.isArray(res) && res.length > 0) {
          const mapped = res.map((addr) => ({
            id: addr.addressId,
            title: addr.addressType,
            description: `${addr.addressLine1}${addr.addressLine2 ? ", " + addr.addressLine2 : ""}, ${addr.city}, ${addr.stateName}, ${addr.countryName}${addr.postalCode ? ", " + addr.postalCode : ""}`,
            addressLine1: addr.addressLine1,
            addressLine2: addr.addressLine2,
            street: addr.street,
            addressTypeId: addr.addressTypeId,
            city: addr.city,
            countryId: addr.countryId,
            isActive: addr.isActive,
            postalCode: addr.postalCode,
            stateProvinceId: addr.stateProvinceId,
          }));
          setAddressList(mapped);
        }

        setIsLoading(false);
      }
    });
  };

  const handleSubmit = (values) => {
    const url = `${apiConfig.OfficeOutlet}/${selectedBranchKey.id}`;
    const body = {
      parentId: values.parentBranch,
      name: values.name,
      externalId: values.externalId || "",
      openingDate: values.openedOn
        ? dayjs(values.openedOn).format("DD MMMM YYYY")
        : null,
      officeTypeId: values.officeTypeId,
      nearestBranchId: values.nearestBranchId,
      address: addressList,
      method: "PUT",
      session,
      locale: "en",
      dateFormat: "dd MMMM yyyy",
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Branch Updated Successfully");
        navigate("/sya0200");
        setIsLoading(false);
      }
    });
  };

  const onCancel = () => navigate("/sya0200", { replace: false });

  const handleAddressUpdate = (updatedAddress, done) => {
    const { id, title, addressTypeId, description, ...rest } = updatedAddress;
    const url = `${apiConfig.AddressOffice}/${selectedBranchKey.id}/addresses`;
    const body = {
      ...rest,
      addressId: id,
      addressType: title,
      method: "PUT",
      session,
      locale: "en",
    };

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage || err?.defaultUserMessage,
        );
        setIsLoading(false);
      } else {
        BOM.AlertMsg(cnt.SUCCESS, "Address updated successfully");
        if (done) done();
        getAddressById(selectedBranchKey.id);
      }
    });
  };

  const handleAddressDelete = (item) => {
    setAddressList(addressList.filter((a) => a.id !== item.id));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ paddingTop: 20 }}
      onFinish={handleSubmit}
    >
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#9ca3af",
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Branch Information
        </div>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="name"
              label={<Text strong>Branch</Text>}
              rules={[{ required: true, message: "Required Field" }]}
            >
              <Input placeholder="Enter branch name" style={{ height: 44 }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="parentBranch"
              label={<Text strong>Parent Branch</Text>}
              rules={[{ required: true, message: "Required Field" }]}
            >
              <Select
                placeholder="Select a parent branch"
                style={{ height: 44 }}
                options={parentBranch}
                showSearch
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="officeTypeId"
              label={<Text strong>Office Type</Text>}
              rules={[{ required: true, message: "Required Field" }]}
            >
              <Select
                placeholder="Select an Option"
                style={{ height: 44 }}
                options={officeTypeOptions}
                showSearch
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="openedOn"
              label={<Text strong>Opened On</Text>}
              rules={[{ required: true, message: "Required Field" }]}
            >
              <DatePicker
                style={{ width: "100%", height: 44 }}
                disabledDate={disabledDate}
                format="DD MMMM YYYY"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="externalId"
              label={<Text strong>Branch Code</Text>}
            >
              <Input placeholder="Enter branch code" style={{ height: 44 }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="nearestBranchId"
              label={<Text strong>Nearest Branch</Text>}
            >
              <Select
                placeholder="Select an Option"
                style={{ height: 44 }}
                options={nearestBranchOptions}
                showSearch
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#9ca3af",
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Address Information
        </div>

        <AddressModal
          countryIdData={countryIdData}
          stateProvinceIdData={stateProvinceIdData}
          addressTypeIdData={addressTypeIdData}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          addressList={addressList}
          setAddressList={setAddressList}
          BOM={BOM}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
          onEditSubmit={handleAddressUpdate}
        />

        {addressList.length > 0 && (
          <List
            style={{ marginTop: 16 }}
            dataSource={addressList}
            renderItem={(item, index) => (
              <Card
                style={{
                  width: "100%",
                  marginBottom: 12,
                  borderRadius: 12,
                  border: "1px solid #e5e7eb",
                }}
                bodyStyle={{ padding: "12px 16px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div
                      style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}
                    >
                      {item.title?.toUpperCase()}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      {item.description}
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Button
                      type="link"
                      onClick={() => setEditingIndex(index)}
                      icon={<EditOutlined />}
                      style={{ padding: 0 }}
                    >
                      Edit
                    </Button>
                    <Button
                      type="link"
                      danger
                      onClick={() => handleAddressDelete(item)}
                      icon={<DeleteOutlined />}
                      style={{ padding: 0 }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          />
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Divider />
          <Row>
            <CustomButton
              content="Submit"
              htmlType="submit"
              style={{
                width: "10%",
                marginLeft: "auto",
                boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
              }}
              backgroundColor="#15CCAE"
              colorText="black"
              hoverBackground="#4FECA0"
            />
            <CustomButton
              content="Cancel"
              style={{
                width: "10%",
                marginLeft: "1%",
                boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
              }}
              backgroundColor="#fff"
              colorText="black"
              hoverBackground="#F5F5F5"
              onClick={onCancel}
            />
          </Row>
        </Form.Item>
      </Card>
    </Form>
  );
};

export default EditBranchForm;
